import { useState, useEffect } from 'react';
import { apiClient } from '../lib/api-client';

interface SessionInfo {
  sessionId: string;
  agentName: string;
  organizationName: string;
  estimatedDuration: number;
}

export default function CustomerJoin() {
  const [sessionCode, setSessionCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [sessionInfo, setSessionInfo] = useState<SessionInfo | null>(null);
  const [hasPermissions, setHasPermissions] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState('');
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [peerConnection, setPeerConnection] = useState<RTCPeerConnection | null>(null);

  // Get session code from URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (code) {
      setSessionCode(code);
      validateSession(code);
    }
  }, []);

  const validateSession = async (code: string) => {
    if (code.length !== 6) return;
    
    setIsValidating(true);
    setError('');
    
    try {
      const response = await apiClient.getSessionByCode(code);
      
      if (response.success && response.data) {
        // Get agent info (mock for now)
        setSessionInfo({
          sessionId: response.data.id,
          agentName: 'Support Agent', // TODO: Get actual agent name
          organizationName: 'StreamShare', // TODO: Get actual org name
          estimatedDuration: 15
        });
      } else {
        setError('Invalid session code. Please check with your support agent.');
      }
    } catch (error) {
      console.error('Session validation error:', error);
      setError('Failed to validate session. Please try again.');
    } finally {
      setIsValidating(false);
    }
  };

  const requestPermissions = async () => {
    try {
      // Request camera and microphone permissions
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: true, 
        audio: true 
      });
      
      // Stop the stream immediately - we just needed to request permission
      stream.getTracks().forEach(track => track.stop());
      setHasPermissions(true);
    } catch (err) {
      console.error('Permission denied:', err);
      setError('Camera and microphone access is required for the video session. Please allow access and try again.');
    }
  };

  const joinSession = async () => {
    setIsConnecting(true);
    
    try {
      if (!sessionInfo) {
        throw new Error('No session info available');
      }

      // Get Cloudflare Realtime token for this session
      const tokenResponse = await fetch(`https://streamshare-functions-preview-preview.iserialize.workers.dev/api/sessions/realtime-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          session_id: sessionInfo.sessionId,
          participant_name: 'Customer',
          participant_type: 'customer'
        })
      });

      if (!tokenResponse.ok) {
        throw new Error('Failed to get session token');
      }

      const tokenData = await tokenResponse.json();

      // Initialize WebRTC connection using native WebRTC APIs
      await initializeWebRTCConnection(tokenData);
      
    } catch (error) {
      console.error('Failed to join session:', error);
      setError('Failed to connect to video session. Please try again.');
      setIsConnecting(false);
    }
  };

  const initializeWebRTCConnection = async (tokenData: any) => {
    try {
      // Create RTCPeerConnection with Cloudflare STUN servers
      const peerConnection = new RTCPeerConnection({
        iceServers: tokenData.ice_servers || [
          { urls: 'stun:stun.cloudflare.com:3478' }
        ]
      });

      // Get user media (camera and microphone)
      const localStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment', // Use rear camera on mobile
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: true
      });

      // Store references for later use
      setLocalStream(localStream);
      setPeerConnection(peerConnection);

      // Add local stream to peer connection
      localStream.getTracks().forEach(track => {
        peerConnection.addTrack(track, localStream);
      });

      // Display local video
      const videoElement = document.getElementById('customerVideo') as HTMLVideoElement;
      if (videoElement) {
        videoElement.srcObject = localStream;
      }

      // Handle remote stream
      peerConnection.ontrack = (event) => {
        console.log('Received remote track:', event.track.kind);
        const remoteVideoElement = document.getElementById('agentVideo') as HTMLVideoElement;
        if (remoteVideoElement && event.streams[0]) {
          remoteVideoElement.srcObject = event.streams[0];
        }
      };

      // Handle ICE candidates
      peerConnection.onicecandidate = (event) => {
        if (event.candidate) {
          console.log('ICE candidate:', event.candidate);
          // In a real implementation, you would send this to the Cloudflare Realtime API
          // For now, we'll log it
        }
      };

      // Handle connection state changes
      peerConnection.onconnectionstatechange = () => {
        console.log('Connection state:', peerConnection.connectionState);
        if (peerConnection.connectionState === 'connected') {
          setIsConnecting(false);
          // Connection successful - update UI to show video call interface
          showVideoCallInterface();
        }
      };

      // Create offer and start connection process
      const offer = await peerConnection.createOffer();
      await peerConnection.setLocalDescription(offer);

      // For demo purposes, we'll simulate a successful connection
      // In a real implementation, you would:
      // 1. Send the offer to Cloudflare Realtime API
      // 2. Receive answer from the API
      // 3. Set remote description
      // 4. Exchange ICE candidates

      console.log('WebRTC offer created:', offer);
      
      // Simulate successful connection after 2 seconds
      setTimeout(() => {
        if (peerConnection.connectionState !== 'connected') {
          // Manually trigger connection state for demo
          setIsConnecting(false);
          showVideoCallInterface();
        }
      }, 2000);

    } catch (error) {
      console.error('WebRTC initialization failed:', error);
      throw error;
    }
  };

  const showVideoCallInterface = () => {
    // Update the UI to show the video call interface
    const container = document.querySelector('.container');
    if (container) {
      container.innerHTML = `
        <div class="bg-white rounded-xl shadow-lg p-6">
          <div class="text-center mb-6">
            <h1 class="text-2xl font-bold text-gray-900 mb-2">Connected to Support Agent</h1>
            <div class="flex items-center justify-center space-x-2">
              <div class="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span class="text-sm font-medium text-gray-700">Live Session</span>
            </div>
          </div>
          
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div class="relative">
              <video id="customerVideo" autoplay muted playsinline class="w-full h-48 bg-gray-900 rounded-lg object-cover"></video>
              <div class="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-xs">
                You
              </div>
            </div>
            <div class="relative">
              <video id="agentVideo" autoplay playsinline class="w-full h-48 bg-gray-900 rounded-lg object-cover"></video>
              <div class="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-xs">
                Support Agent
              </div>
            </div>
          </div>
          
          <div class="flex justify-center space-x-4">
            <button id="muteBtn" onclick="window.toggleMute?.()" class="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors">
              🎤 Mute
            </button>
            <button id="videoBtn" onclick="window.toggleVideo?.()" class="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors">
              📹 Video
            </button>
            <button onclick="window.endCall?.()" class="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors">
              📞 End Call
            </button>
          </div>
          
          <div class="mt-4 text-center text-sm text-gray-500">
            Point your camera at the issue you're experiencing
          </div>
        </div>
      `;
      
      // Set up global functions for video controls
      setupVideoControls();
    }
  };

  const setupVideoControls = () => {
    let isMuted = false;
    let isVideoEnabled = true;

    (window as any).toggleMute = () => {
      if (localStream) {
        const audioTrack = localStream.getAudioTracks()[0];
        if (audioTrack) {
          audioTrack.enabled = !audioTrack.enabled;
          isMuted = !audioTrack.enabled;
          
          const btn = document.getElementById('muteBtn');
          if (btn) {
            btn.textContent = isMuted ? '🔇 Unmute' : '🎤 Mute';
            btn.className = isMuted 
              ? 'bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors'
              : 'bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors';
          }
        }
      }
    };

    (window as any).toggleVideo = () => {
      if (localStream) {
        const videoTrack = localStream.getVideoTracks()[0];
        if (videoTrack) {
          videoTrack.enabled = !videoTrack.enabled;
          isVideoEnabled = videoTrack.enabled;
          
          const btn = document.getElementById('videoBtn');
          if (btn) {
            btn.textContent = isVideoEnabled ? '📹 Video' : '📹 Enable';
            btn.className = isVideoEnabled 
              ? 'bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors'
              : 'bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors';
          }
        }
      }
    };

    (window as any).endCall = () => {
      if (localStream) {
        localStream.getTracks().forEach(track => track.stop());
      }
      if (peerConnection) {
        peerConnection.close();
      }
      
      // Show end call message
      const container = document.querySelector('.container');
      if (container) {
        container.innerHTML = `
          <div class="bg-white rounded-xl shadow-lg p-6 text-center">
            <div class="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
              <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 class="text-2xl font-bold text-gray-900 mb-2">Session Ended</h1>
            <p class="text-gray-600 mb-4">Thank you for using StreamShare support!</p>
            <p class="text-sm text-gray-500">
              You can close this window. If you need further assistance, 
              please contact your support agent for a new session.
            </p>
          </div>
        `;
      }
    };
  };

  const handleCodeSubmit = () => {
    if (sessionCode.length === 6) {
      validateSession(sessionCode);
    } else {
      setError('Please enter a valid 6-character session code');
    }
  };

  if (!sessionInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Join Support Session</h1>
              <p className="text-gray-600">Enter the 6-character code provided by your support agent</p>
            </div>

            <div className="mb-6">
              <label htmlFor="sessionCode" className="block text-sm font-medium text-gray-700 mb-2">
                Session Code
              </label>
              <input
                type="text"
                id="sessionCode"
                value={sessionCode}
                onChange={(e) => setSessionCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6))}
                placeholder="ABC123"
                className="w-full px-4 py-4 text-center text-xl font-mono border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent tracking-widest"
                maxLength={6}
              />
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex">
                  <svg className="w-5 h-5 text-red-400 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            )}

            <button
              onClick={handleCodeSubmit}
              disabled={sessionCode.length !== 6 || isValidating}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isValidating ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Validating...
                </div>
              ) : 'Join Session'}
            </button>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">
                Need help? Contact your support agent
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          {/* Session Info Header */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Ready to Connect</h1>
                <p className="text-gray-600">Session #{sessionInfo.sessionId}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Your support agent</p>
                <p className="text-lg font-semibold text-gray-900">{sessionInfo.agentName}</p>
                <p className="text-sm text-gray-500">{sessionInfo.organizationName}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4 text-sm text-gray-600">
              <div className="flex items-center">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Estimated duration: {sessionInfo.estimatedDuration} minutes
              </div>
              <div className="flex items-center">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                Secure & Private
              </div>
            </div>
          </div>

          {/* Permission Check */}
          {!hasPermissions ? (
            <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-yellow-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">Camera & Microphone Access</h2>
                <p className="text-gray-600 mb-6">
                  We need access to your camera and microphone to start the video session with your support agent.
                </p>
                
                <div className="bg-blue-50 rounded-lg p-4 mb-6">
                  <div className="flex">
                    <svg className="w-5 h-5 text-blue-400 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div className="text-left">
                      <p className="text-sm font-medium text-blue-900">Your privacy is protected</p>
                      <p className="text-sm text-blue-700">
                        Your camera and microphone will only be active during the support session. No recordings are made without your consent.
                      </p>
                    </div>
                  </div>
                </div>

                <button
                  onClick={requestPermissions}
                  className="bg-blue-600 text-white py-3 px-8 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                >
                  Allow Camera & Microphone
                </button>
              </div>
            </div>
          ) : (
            /* Ready to Connect */
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">Ready to Connect!</h2>
                <p className="text-gray-600 mb-6">
                  Everything is set up. Click the button below to start your video session with {sessionInfo.agentName}.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  <div className="flex flex-col items-center p-4 bg-gray-50 rounded-lg">
                    <svg className="w-6 h-6 text-blue-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <span className="text-sm font-medium text-gray-700">HD Video Call</span>
                  </div>
                  <div className="flex flex-col items-center p-4 bg-gray-50 rounded-lg">
                    <svg className="w-6 h-6 text-green-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                    </svg>
                    <span className="text-sm font-medium text-gray-700">Screen Sharing</span>
                  </div>
                  <div className="flex flex-col items-center p-4 bg-gray-50 rounded-lg">
                    <svg className="w-6 h-6 text-purple-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                    </svg>
                    <span className="text-sm font-medium text-gray-700">Live Chat</span>
                  </div>
                </div>

                <button
                  onClick={joinSession}
                  disabled={isConnecting}
                  className="bg-green-600 text-white py-4 px-8 rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-lg"
                >
                  {isConnecting ? (
                    <div className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Connecting...
                    </div>
                  ) : 'Start Video Session'}
                </button>

                {error && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
